import { useState, useEffect, useCallback } from 'react';
import { getApiBaseUrl } from '../../../../core/config/api';

/**
 * Type partagé pour une intervention CTA (liste panel).
 * Utilisé par InterventionsManage, InterventionsCreate (évolutions), etc.
 */
export interface InterventionCTA {
  id: number;
  service: string;
  categorie: string;
  statut: string;
  /** 1 / 0 selon la base (colonne is_exceptionnelle) */
  is_exceptionnelle?: number | boolean;
  created_at: string;
  updated_at: string;
  created_by_username?: string | null;
  last_modified_at?: string | null;
  last_modified_by_username?: string | null;
  validated_at?: string | null;
  validated_by_username?: string | null;
  titres?: string[];
}

export interface UseInterventionsListReturn {
  interventions: InterventionCTA[];
  loading: boolean;
  error: string | null;
  reload: () => Promise<void>;
}

/**
 * Hook partagé : charge la liste de toutes les interventions créées via le panel.
 * À utiliser pour la liste (Gérer les scénarios), les évolutions de mission, etc.
 */
export function useInterventionsList(): UseInterventionsListReturn {
  const [interventions, setInterventions] = useState<InterventionCTA[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setError('Token d\'authentification manquant');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const apiUrl = getApiBaseUrl();
      const response = await fetch(`${apiUrl}/api/cta/interventions/list`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        credentials: 'include',
      });

      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        console.error('Réponse non-JSON (interventions/list):', text.substring(0, 200));
        setError('Réponse serveur invalide.');
        setLoading(false);
        return;
      }

      const data = await response.json();
      if (data.success) {
        setInterventions(Array.isArray(data.data) ? data.data : []);
      } else {
        setError(data.error || 'Erreur lors du chargement des scénarios');
      }
    } catch (err: any) {
      console.error('Erreur chargement interventions:', err);
      setError(err.message || 'Erreur lors du chargement des scénarios');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return { interventions, loading, error, reload: load };
}
